/**
 * Afterpay button — redirect flow.
 *
 * When Afterpay is enabled (env vars set on the backend), clicking this
 * button POSTs to `/api/shop/afterpay/create-checkout`, then redirects
 * the browser to the Afterpay hosted checkout.  After approval the user
 * returns to `/checkout/afterpay-return?kr_order_id=...&orderToken=...&status=SUCCESS`
 * which triggers the capture on the backend.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { shopPaymentsAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { toast } from 'sonner';

export default function AfterpayPayButton({ userEmail, userFirstName, disabled }) {
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState(userEmail || '');
  const [firstName, setFirstName] = useState(userFirstName || '');

  const start = async () => {
    if (!email || !firstName) {
      return toast.error('Email and first name are required for Afterpay');
    }
    setBusy(true);
    try {
      const r = await shopPaymentsAPI.afterpayCreateCheckout({
        email, given_name: firstName,
      });
      window.location.assign(r.data.redirect_url);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to start Afterpay'));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="afterpay-section">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">First name</Label>
          <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="afterpay-firstname" />
        </div>
        <div>
          <Label className="text-xs">Email</Label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" data-testid="afterpay-email" />
        </div>
      </div>
      <Button onClick={start} disabled={busy || disabled} className="w-full bg-black text-white hover:bg-slate-800" data-testid="afterpay-pay-btn">
        {busy && <Loader2 size={13} className="mr-1.5 animate-spin" />}
        Continue with Afterpay
      </Button>
    </div>
  );
}
